/** GIF picker (D-008 — server-proxied, keyless; curated library by default). */
import { useEffect, useRef, useState } from "react";
import { resolveCopy } from "@humble/shared";
import { Sheet } from "../../design/index.js";
import { api, isApiError } from "../../lib/api.js";
import type { GifWire } from "../conversations/types.js";

export function GifPicker({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (gif: GifWire) => void;
}) {
  const [query, setQuery] = useState("");
  const [gifs, setGifs] = useState<GifWire[]>([]);
  const [error, setError] = useState<string | null>(null);
  const debounce = useRef<number>();

  useEffect(() => {
    if (!open) return;
    setError(null);
    window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => {
      void (async () => {
        try {
          const res = query.trim()
            ? await api.get("/gifs/search", { query: { q: query.trim() } })
            : await api.get("/gifs/trending");
          setGifs((res as unknown as GifWire[]) ?? []);
        } catch (err) {
          if (isApiError(err, "RATE_LIMITED")) setError(err.message);
          else setError(resolveCopy("errors.slow") ?? "Search failed.");
        }
      })();
    }, 250);
    return () => window.clearTimeout(debounce.current);
  }, [open, query]);

  return (
    <Sheet open={open} onClose={onClose} title="Reactions">
      <input
        className="gifpicker__search"
        type="search"
        autoFocus
        placeholder={resolveCopy("chat.gif_search_placeholder")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search GIFs"
      />
      {error && <p className="text-dim">{error}</p>}
      <div className="gifpicker__grid">
        {gifs.map((g) => (
          <button key={g.id} className="gifpicker__cell" onClick={() => onPick(g)} title={g.title}>
            <img src={g.preview_url} alt={g.title} loading="lazy" />
          </button>
        ))}
        {gifs.length === 0 && !error && (
          <p className="text-faint gifpicker__empty">{resolveCopy("chat.gif_empty")}</p>
        )}
      </div>
    </Sheet>
  );
}
